import type { Ctx } from 'boardgame.io';
import { debug } from '../utils/debug';
import type {
  PuertoRicoGameState,
  PuertoRicoPlayer,
  PuertoRicoDifficulty,
  PlayerId,
  GoodsDisplay
} from '../types';
import { DIFFICULTY_CUBES, PLAYER_COLORS } from '../types';
import { PUERTO_RICO_MAP_CONFIG } from '../../data/map/puerto_rico/config';
import { loadPuertoRicoMap } from './utils/mapLoader';
import {
  initializePuertoRicoCubes,
  placePuertoRicoInitialGoods
} from './utils/goodsSetup';

/** localStorage キー（GameStartModal と共有） */
const DIFFICULTY_STORAGE_KEY = 'puerto_rico_difficulty';

/**
 * localStorage から難易度を取得
 * @returns 保存された難易度、または デフォルト 'Expert'
 */
function getDifficultyFromStorage(): PuertoRicoDifficulty {
  // ブラウザ環境チェック
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    debug.log('[getDifficultyFromStorage] Not in browser environment, using default');
    return 'Expert';
  }
  const stored = localStorage.getItem(DIFFICULTY_STORAGE_KEY);
  debug.log(`[getDifficultyFromStorage] localStorage value: "${stored}"`);
  if (stored && ['Beginner', 'Novice', 'Expert', 'Master', 'Conqueror', 'God'].includes(stored)) {
    return stored as PuertoRicoDifficulty;
  }
  return 'Expert';
}

/**
 * Puerto Rico ソロプレイ用のゲーム初期状態を生成
 *
 * 難易度の決定順序:
 * 1. 引数で渡された場合はそれを使用（テスト用）
 * 2. localStorage に保存されている場合はそれを使用（UI経由）
 * 3. デフォルトは 'Expert'
 *
 * @param _ctx Boardgame.io コンテキスト（未使用）
 * @param difficultyArg 難易度（テスト用、省略時は localStorage から取得）
 * @returns 初期ゲーム状態（PuertoRicoGameState）
 */
export function setupPuertoRicoGame(
  _ctx: Ctx,
  difficultyArg?: PuertoRicoDifficulty
): PuertoRicoGameState {
  // 引数優先、次に localStorage、最後にデフォルト
  const difficulty = difficultyArg ?? getDifficultyFromStorage();
  // Puerto Rico Solo Play: プレイヤーは常に1人（ID='0'）
  const playerId: PlayerId = '0';

  // Puerto Rico プレイヤー初期化
  const soloPlayer: PuertoRicoPlayer = {
    id: playerId,
    name: 'Solo Player',
    color: PLAYER_COLORS[0],     // プレイヤーカラー
    cash: 10,                    // 初期資金 $10
    incomeLevel: 0,              // 収入レベル 0
    sharesIssued: 2,             // 初期株式数 2
    locomotiveLevel: 1,          // 初期機関車レベル 1リンク
    selectedAction: null,        // 未選択
    playerOrder: 1,              // ソロプレイなので常に1
    isBankrupt: false,           // 倒産していない
    orderBidAmount: 0,           // Phase 2未使用（ソロプレイ）
    tilesPlacedThisTurn: 0,      // Phase 4: 今ターン配置したタイル数
    hasPassedBuild: false,       // Phase 4: 手番終了フラグ
    buildTrackHistory: [],       // Phase 4: Cancel操作用の配置履歴
    hasUpgradedLocoThisTurn: false,  // Phase 5: 今ターン機関車強化済みフラグ
    goodsTransportedThisPhase: 0,    // Phase 5: 輸送回数
    turnsCompletedThisPhase: 0,  // Phase 5: 完了した手番数（0, 1, 2）
    goodsPlacedThisTurn: 0,      // Phase 9: 未使用（Puerto Rico ではスキップ）
    newCityTilesPlacedThisTurn: 0, // Phase 4: 今ターン配置した新都市タイル数

    // Puerto Rico 固有プロパティ
    currentTurnSharesIssued: 0,  // 今ターン発行した株式数
    hasExecutedIssueSharesMove: false,  // 株式発行Move実行済みフラグ
    finalScore: null,            // 最終スコア（ゲーム終了前はnull）
    isVictorious: false          // 勝利フラグ
  };

  // プレイヤーマップ
  const players: Record<PlayerId, PuertoRicoPlayer> = {
    [playerId]: soloPlayer
  };

  // マップデータ読み込み（Puerto Rico）
  let map = loadPuertoRicoMap();

  // 難易度に応じたキューブ数を取得
  const cubeConfig = DIFFICULTY_CUBES[difficulty];
  debug.log(`[setupPuertoRicoGame] Difficulty: ${difficulty}, Red: ${cubeConfig.red}, Black: ${cubeConfig.black}`);

  // 商品キューブ初期化（赤と黒のみ、難易度で数量決定）
  let cubesInBag = initializePuertoRicoCubes(cubeConfig.red, cubeConfig.black);

  // 商品補充表は使用しない（null）
  const goodsDisplay: GoodsDisplay | null = null;

  // マップ上に初期商品配置（各町に2個ずつ）
  const { updatedMap, remainingCubes } = placePuertoRicoInitialGoods(map, cubesInBag);
  map = updatedMap;
  cubesInBag = remainingCubes;

  debug.log(`[setupPuertoRicoGame] Map loaded: ${map.length} hexes, ${cubesInBag.length} cubes in bag`);

  // Puerto Rico ゲーム状態初期化
  return {
    mapName: 'Puerto Rico',
    mapConfig: PUERTO_RICO_MAP_CONFIG,
    players,
    map,
    goodsDisplay,
    cubesInBag,
    removedCubes: [],
    completedLinks: [],            // 完成区間なし
    unfinishedSections: [],        // 未完成区間なし
    ferryLinks: [],                // Puerto Rico: フェリーリンクなし
    adjacentLinks: [],             // Puerto Rico: 隣接リンクなし
    isGameOver: false,             // ゲーム継続中
    undoCount: 0,                  // Undo to Turn Start 実行回数
    endReason: null,
    finalScores: {},

    // Puerto Rico 固有プロパティ
    difficulty,                    // 選択した難易度
    actualTurn: 0,                 // ゲームターン初期値は0（Phase 1で+1される）
    isActionSelectionComplete: false,  // アクション選択完了フラグ

    // Town Disk 管理（共通）
    townDisksRemaining: 8,         // Town Disk 残り枚数（初期値8）
    townDiskPlaced: {},            // 各 Town への配置記録

    // 自動フェーズ用
    autoPhaseResult: null,         // 自動フェーズ処理結果
    completedAutoPhases: [],       // 完了した自動フェーズ履歴

    // ML学習用
    moveLog: []                    // Move-Level Replay Log
  };
}
